import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Routes from '../../../routes';
import UserInfo from '../../../UserInfo';
import {
    DateInput,
    Select,
    MultiSelect,
    Input,
    PageTitleWithToolTip,
    LoadingButtonV4 as LoadingButton,
} from '../../Common';
import { useNavigate } from '../../../hooks';
import { isDesktop, postRequestHeaders } from '../../../utils';
import { Grid, Paper } from '@mui/material';
import { trakrefBlue, trakrefYellow } from "../../../color_utils";
import Enums from "../../../enums";
import { renderWeight } from "../../../Utils/weight_utils";
import { formatDateWithZ } from '../../../utils';
import SiteServiceLayout from '../Shared/SiteServiceLayout';
import SystemRow from '../Shared/SystemRow';
import SystemFilterBar from '../Shared/SystemFilterBar';
import SystemSelectionControls from '../Shared/SystemSelectionControls';
import CompletionModal from '../Shared/CompletionModal';
import ShutdownFields from './ShutdownFields';
import { paperResponsiveStyle } from '../Shared/styles';

const shutdownTypes = [{ id: 5, label: "Shutdown/Mothball" }];
const systemTypeFilters = [
    { id: 31, name: "Comfort Cooling" },
    { id: 63, name: "Process Cooling" },
    { id: 22, name: "Refrigeration" }
];
const systemStatusFilters = [
    { id: 1, name: "Active" },
    { id: 2, name: "Inactive" }
];

const excludedStatuses = [
    Enums.SE.CoolingApplianceStatus.Retrofitted,
    Enums.SE.CoolingApplianceStatus.Moved,
    Enums.SE.CoolingApplianceStatus.DisposedDestroyed,
    Enums.SE.CoolingApplianceStatus.Sold,
    Enums.SE.CoolingApplianceStatus.ProvisionalNonOperation,
];

const postShutdownStatusIds = [
    Enums.SE.CoolingApplianceStatus.ShutdownRetired,
    Enums.SE.CoolingApplianceStatus.DisposedDestroyed,
    Enums.SE.CoolingApplianceStatus.Sold,
];

const weightPrecision = UserInfo.getInstance(UserInfo.getInstanceID()) ? UserInfo.getInstance(UserInfo.getInstanceID()).weightPrecision : null;

const initShutdownDetail = () => ({
    wasRefrigerantRecovered: null,
    postShutdownStatusId: null,
    cylinderId: null,
    transferAmount: null,
    transferAmountError: null,
    recoverGasStatusId: null,
    cylinderNotes: '',
    transferNotes: '',
    recoveryEquipmentId: null,
    wasProperVacuumPulled: null,
    vacuumPsi: null,
});

const getChipData = (asset) => {
    const {
        coolingAppliance: { coolingApplianceStatusId = null } = {},
        isChecked,
        shutdownDetail: { wasRefrigerantRecovered, postShutdownStatusId } = {},
    } = asset;

    let chipData = {
        chipLabel: "No Change",
        color: "#FFF",
        chipColor: trakrefBlue,
        disabled: false
    };

    if (coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.UnderRepair) {
        chipData = {
            chipLabel: "Under Repair",
            chipColor: trakrefYellow,
        };
    }

    if (isChecked && wasRefrigerantRecovered === null) {
        chipData.chipLabel = "Pending";
    }

    if (isChecked && postShutdownStatusId) {
        const fullLabel = asset._postShutdownStatusLabel || "Status Set";
        chipData.chipLabel = !isDesktop && fullLabel.toLowerCase().startsWith("mothball") ? "Mothball" : fullLabel;
    }

    return chipData;
};

const buildInventoryData = (asset) => {
    const {
        tagNumber = '',
        serialNumber = '',
        installDate = '',
        assetType: { name: assetTypeName = '' } = {},
        coolingAppliance: {
            gasChargeCapacityLbs = null,
            currentGasWeightLbs = null,
            materialType: { name: materialTypeName = '' } = {},
        } = {},
    } = asset;

    return [
        { label: "System Type", value: assetTypeName },
        { label: "Tag Number", value: tagNumber },
        { label: "Gas Capacity", value: renderWeight(gasChargeCapacityLbs, weightPrecision || 3, weightPrecision || 2) },
        { label: "Current Charge", value: renderWeight(currentGasWeightLbs, weightPrecision || 3, weightPrecision || 2) },
        { label: "Material Type", value: materialTypeName },
        { label: "Serial Number", value: serialNumber },
        { label: "Install Date", value: formatDateWithZ(installDate) }
    ];
};

const SiteShutdown = (props) => {
    const workOrderId = props.match.params.id;
    const [shutdownDate, setShutdownDate] = useState(null);
    const [primaryTechnician, setPrimaryTechnician] = useState(null);
    const [serviceAction, setServiceAction] = useState(null);
    const [notes, setNotes] = useState('');
    const [systemTypes, setSystemTypes] = useState([
        Enums.SE.AssetType.ComfortCooling,
        Enums.SE.AssetType.ProcessCooling,
        Enums.SE.AssetType.Refrigeration
    ]);
    const [systemStatus, setSystemStatus] = useState(systemStatusFilters.map(s => s.id));
    const [assets, setAssets] = useState([]);
    const [originalAssets, setOriginalAssets] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dropDownOptions, setDropDownOptions] = useState({
        technicians: [],
        serviceActions: [],
        recoveryEquipment: [],
        recoverGasStatuses: [],
        coolingApplianceStatuses: []
    });
    const navigate = useNavigate({ pageName: 'SiteShutdown' });

    const fetchData = async () => {
        const [leakResponse, dropdownResponse] = await Promise.all([
            fetch(Routes.ServiceEvent.SiteLeakInspectionDropdown(workOrderId)),
            fetch(Routes.ServiceEvent.AddServiceEventDropdowns(workOrderId, 0))
        ]);

        const leakInfo = await leakResponse.json();
        const dropdownInfo = await dropdownResponse.json();

        const { coolingAppliance } = leakInfo;

        const {
            serviceActions = [],
            recoveryEquipment = [],
            recoverGasStatuses = [],
            coolingApplianceStatuses = [],
            technicians: serviceEventTechnicians = [],
        } = dropdownInfo;

        const technicians = serviceEventTechnicians.filter((obj) => obj.validCertificateNumber === true);
        const eligibleAssets = coolingAppliance
            .map(({ asset }) => ({
                ...asset,
                shutdownDetail: initShutdownDetail(),
                cylinders: []
            }))
            .filter(a =>
                a.coolingAppliance &&
                !excludedStatuses.includes(a.coolingAppliance.coolingApplianceStatusId)
            );

        setAssets(eligibleAssets);
        setOriginalAssets(eligibleAssets);
        setDropDownOptions({
            technicians,
            serviceActions,
            recoveryEquipment,
            recoverGasStatuses,
            coolingApplianceStatuses
        });
    };

    const getFilteredData = () => {
        let filtered = [...originalAssets].map(a => ({
            ...a,
            isChecked: false,
            shutdownDetail: initShutdownDetail(),
            cylinders: []
        }));

        if (systemTypes.length < systemTypeFilters.length) {
            filtered = filtered.filter(a => systemTypes.includes(a.assetType.id));
        }

        if (systemStatus.length < systemStatusFilters.length) {
            filtered = filtered.filter(a => systemStatus.includes(a.assetStatusId));
        }

        setAssets(filtered);
    };

    const fetchCylindersForAsset = async (index, assetId) => {
        if (!primaryTechnician) return;

        const response = await fetch(
            Routes.Asset.FetchRecoveryCylinders(assetId, primaryTechnician),
            { method: 'POST', headers: { ...postRequestHeaders } }
        );
        const cylinders = await response.json();

        return [{ id: 0, label: "Unknown Cylinder" }, ...cylinders];
    };

    const handleCheck = async (index, e) => {
        const isChecked = e.target.checked;
        const assetsClone = [...assets];
        let cylinders = [];

        if (isChecked && primaryTechnician) {
            cylinders = await fetchCylindersForAsset(index, assetsClone[index].id);
        }
        
        assetsClone[index] = {
            ...assetsClone[index],
            isChecked,
            ...(isChecked && { cylinders }),
            ...(!isChecked && {
                shutdownDetail: initShutdownDetail(),
                cylinders: []
            })
        };
        
        setAssets(assetsClone);
    };

    const handleSelectAll = async () => {
        // Set checkbox immediately — select every visible (already-filtered) system,
        // including Under Repair, which is eligible per US 10905 AC3/AC4.
        const checkedAssets = assets.map(a => ({ ...a, isChecked: true }));
        setAssets(checkedAssets);

        // Fetch & Set cylinders in the background
        if (primaryTechnician) {
            const cylinderResults = await Promise.all(
                checkedAssets.map(async (a) => {
                    if (!a.isChecked) return [];
                    return await fetchCylindersForAsset(null, a.id);
                })
            );

            setAssets(prev => prev.map((a, i) => ({
                ...a,
                cylinders: a.isChecked ? cylinderResults[i] : []
            })));
        }
    };

    const handleDeselectAll = () => {
        const sanitizedAssets = assets.map(a => ({
            ...a,
            isChecked: false,
            shutdownDetail: initShutdownDetail(),
            cylinders: []
        }));
        setAssets(sanitizedAssets);
    };

    const setAssetField = (index, field, value) => {
        const assetsClone = [...assets];
        const { coolingAppliance: { currentGasWeightLbs } } = assetsClone[index];
        assetsClone[index] = {
            ...assetsClone[index],
            shutdownDetail: {
                ...assetsClone[index].shutdownDetail,
                [field]: value
            }
        };

        // Look up the status label for chip display
        if (field === "postShutdownStatusId") {
            const status = dropDownOptions.coolingApplianceStatuses.find(s => s.id === value);
            assetsClone[index]._postShutdownStatusLabel = status?.label || status?.name || "Status Set";
        }

        // If wasRefrigerantRecovered changes to false, clear recovery fields
        if (field === "wasRefrigerantRecovered" && value === false) {
            assetsClone[index].shutdownDetail = {
                ...assetsClone[index].shutdownDetail,
                wasRefrigerantRecovered: false,
                cylinderId: null,
                transferAmount: null,
                recoverGasStatusId: null,
                cylinderNotes: '',
                transferNotes: '',
                recoveryEquipmentId: null,
                wasProperVacuumPulled: null,
                vacuumPsi: null,
            };
        }

        // If gas recovery amount is > total gas charge, set error / disable submit
        if (field === "transferAmount") {
            const transferAmountError = value > currentGasWeightLbs;
            assetsClone[index].shutdownDetail = {
                ...assetsClone[index].shutdownDetail,
                transferAmountError,
            };
        }

        setAssets(assetsClone);
    };

    const isSaveDisabled = () => {
        if (!shutdownDate || !primaryTechnician || !serviceAction) {
            return true;
        }

        const includedAssets = assets.filter(a => a.isChecked);
        if (includedAssets.length === 0) {
            return true;
        }

        return includedAssets.some(a => {
            const d = a.shutdownDetail;

            // Must answer the recovery question
            if (d.wasRefrigerantRecovered === null || d.wasRefrigerantRecovered === undefined) {
                return true;
            }

            // Post shutdown status always required
            if (!d.postShutdownStatusId) {
                return true;
            }

            // If recovered, validate recovery fields
            if (d.wasRefrigerantRecovered) {
                if (d.cylinderId === null || d.cylinderId === undefined) return true;
                if (!d.transferAmount || d.transferAmount <= 0) return true;
                if (d.wasProperVacuumPulled === null || d.wasProperVacuumPulled === undefined) return true;
                if (d.cylinderId === 0 && !d.recoverGasStatusId) return true;
                if (d.transferAmountError) return true;
            }

            return false;
        });
    };

    const buildPayload = () => ({
        Id: workOrderId,
        InstanceId: UserInfo.instanceId,
        UserId: UserInfo.userId,
        UserWeightUnits: UserInfo.weightUnits,
        WorkItemTypeId: 5,
        ServiceDate: shutdownDate,
        PrimaryTechnicianId: primaryTechnician,
        ServiceActionId: serviceAction,
        Notes: notes,
        WorkItems: assets.filter(a => a.isChecked).map(a => ({
            AssetId: a.id,
            WasRefrigerantRecovered: a.shutdownDetail.wasRefrigerantRecovered,
            FinalCoolingApplianceStatusId: a.shutdownDetail.postShutdownStatusId,
            RecoveryEquipmentId: a.shutdownDetail.recoveryEquipmentId,
            WasProperVacuumPulled: a.shutdownDetail.wasProperVacuumPulled,
            VacuumPsi: a.shutdownDetail.vacuumPsi,
            Recoveries: a.shutdownDetail.wasRefrigerantRecovered ? [{
                CylinderId: a.shutdownDetail.cylinderId,
                TransferWeightLbs: a.shutdownDetail.transferAmount,
                CylinderNotes: a.shutdownDetail.cylinderNotes,
                TransferNotes: a.shutdownDetail.transferNotes,
                RecoverGasStatusId: a.shutdownDetail.recoverGasStatusId,
            }] : [],
        }))
    });

    const submit = async () => {
        setLoading(true);
        const payload = buildPayload();

        const response = await fetch(Routes.ServiceEvent.AddSiteShutdown(), {
            method: 'POST',
            headers: { ...postRequestHeaders },
            body: JSON.stringify(payload)
        });

        if (response.status === 200) {
            setLoading(false);
            return setModalOpen(true);
        }

        setLoading(false);
        return toast.error('There was an error saving the site shutdown', { duration: 6000 });
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        getFilteredData();
    }, [systemTypes, systemStatus]);

    const postShutdownStatuses = dropDownOptions.coolingApplianceStatuses
        .filter(s => postShutdownStatusIds.includes(s.id));

    return (
        <SiteServiceLayout
            title={`Work Order #${workOrderId}`}
            onClose={() => navigate.goBack(`/WorkOrder/${workOrderId}`)}
            actionButton={
                <LoadingButton
                    useLargeButton
                    buttonText="Complete"
                    fullWidth={!isDesktop}
                    disabled={isSaveDisabled()}
                    loading={loading}
                    onClick={submit}
                />
            }
            alert={{ message: "Select each system included in the site shutdown and identify whether refrigerant was recovered." }}
        >
            <Paper variant="outlined" sx={{ ...paperResponsiveStyle }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} mt={2}>
                        <PageTitleWithToolTip pageTitle="Shutdown Details" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            disabled
                            value={shutdownTypes[0].id}
                            label="Type of Service"
                            variant="outlined"
                            options={shutdownTypes}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DateInput
                            variant="outlined"
                            allowPast
                            allowFuture={false}
                            label="Shutdown Date *"
                            date={shutdownDate}
                            onChange={setShutdownDate}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            required
                            value={primaryTechnician}
                            label="Primary Technician"
                            variant="outlined"
                            options={dropDownOptions.technicians}
                            onChange={(e) => setPrimaryTechnician(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Select
                            required
                            value={serviceAction}
                            label="Service Action"
                            variant="outlined"
                            options={dropDownOptions.serviceActions}
                            onChange={(e) => setServiceAction(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Input
                            fullWidth
                            label="Notes"
                            variant="outlined"
                            multiline
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <SystemFilterBar>
                <Grid item xs={12} md={6}>
                    <MultiSelect
                        renderAllOption
                        value={systemTypes}
                        label="System Type"
                        variant="outlined"
                        options={systemTypeFilters}
                        onChange={(e) => setSystemTypes(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MultiSelect
                        renderAllOption
                        allOptionLabel="Select All"
                        value={systemStatus}
                        label="System Status"
                        variant="outlined"
                        options={systemStatusFilters}
                        onChange={(e) => setSystemStatus(e.target.value)}
                    />
                </Grid>
            </SystemFilterBar>

            <SystemSelectionControls
                hasSelection={assets.some(a => a.isChecked)}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                disabled={loading}
                bulkAction={null}
                helpText="Select each system included in the site shutdown and identify whether refrigerant was recovered."
            />

            <Grid
                container
                spacing={2}
                sx={{
                    mt: 2,
                    mb: { xs: 2, md: 0 },
                }}
            >
                {assets.map((asset, index) => {
                    const { chipLabel, chipColor, disabled } = getChipData(asset);
                    const inventoryData = buildInventoryData(asset);
                    return (
                        <Grid item xs={12} key={index}>
                            <SystemRow
                                asset={asset}
                                isChecked={asset.isChecked || false}
                                onCheck={(e) => handleCheck(index, e)}
                                disabled={disabled || !primaryTechnician}
                                loading={loading}
                                chipLabel={chipLabel}
                                chipColor={chipColor}
                                inventoryData={inventoryData}
                            >
                                <ShutdownFields
                                    isChecked={asset.isChecked || false}
                                    disabled={disabled || !primaryTechnician}
                                    shutdownDetail={asset.shutdownDetail}
                                    onFieldChange={(field, value) => setAssetField(index, field, value)}
                                    dropDownOptions={{
                                        cylinders: asset.cylinders || [],
                                        recoverGasStatuses: dropDownOptions.recoverGasStatuses,
                                        recoveryEquipment: dropDownOptions.recoveryEquipment,
                                        postShutdownStatuses: postShutdownStatuses,
                                    }}
                                />
                            </SystemRow>
                        </Grid>
                    );
                })}
            </Grid>

            <CompletionModal
                open={modalOpen}
                onClose={() => navigate.goBack(`/WorkOrder/${workOrderId}`)}
                header="Site Shutdown created."
                imageAlt="Site Shutdown successfully created."
                message="Site Shutdown has been recorded for all selected systems."
                actionButtonText="VIEW WORK ORDER"
                onAction={() => navigate.goBack(`/WorkOrder/${workOrderId}`)}
            />
        </SiteServiceLayout>
    );
};

export default SiteShutdown;
